/**
 * What this browser keeps in IndexedDB for whoever was signed in, dropped on
 * log out.
 *
 * Log out cleared the session from localStorage but left IndexedDB alone, so
 * the signed-out user's cached conversations and messages (`inmeet-chat`) and
 * any local-recording chunks (`inmeet_recordings`) stayed on the machine for
 * the next person at this browser. The chat cache did notice a different user
 * on the next sign-in, but only once they opened chat — until then the data
 * was there to read.
 *
 * Every database this origin has is deleted, not only the ones named here:
 * anything a library stored for this user goes too. The named ones are
 * covered even where the browser cannot list its databases.
 *
 * Log out no longer waits for any of this before leaving for SecureID's logout
 * page. It used to, for up to 3 s, and every log out took the full 3 s: Firebase
 * keeps its three databases open for the life of the page, so their deletes are
 * blocked until it goes. The login page was on screen the whole time, and a
 * "Sign in" clicked inside that window replaced the SecureID redirect — SecureID
 * stayed signed in and the next sign-in asked for no password. So the deletes
 * are now ISSUED before the redirect, and a marker makes sure they FINISH: it
 * stays set until every delete has completed, and the next page load runs the
 * clear again if it is still there (`resumePendingLocalDataClear`).
 */
const APP_DATABASES = ['inmeet-chat', 'inmeet_recordings']

/** Longest a log out that stays on the page waits on IndexedDB. */
const CLEAR_TIMEOUT_MS = 3000

/**
 * Set before the deletes start, removed once every one of them has completed.
 * Still present on a page load means a log-out clear never finished there — the
 * page navigated away first, or another connection held a database open.
 */
export const CLEAR_PENDING_KEY = 'inmeet_local_data_clear_pending'

type DeleteOutcome = 'deleted' | 'unfinished'

function deleteDatabase(name: string): Promise<DeleteOutcome> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name)
      req.onsuccess = () => resolve('deleted')
      req.onerror = () => resolve('unfinished')
      // Another connection holds it open (Firebase, or this app in another
      // tab). The delete stays queued and runs once that connection closes;
      // nothing waits for it, and the pending marker retries it if it never does.
      req.onblocked = () => resolve('unfinished')
    } catch {
      // IndexedDB refused outright (storage disabled) — there is nothing this
      // origin could have stored, so nothing to retry.
      resolve('deleted')
    }
  })
}

function setPending(pending: boolean) {
  try {
    if (pending) localStorage.setItem(CLEAR_PENDING_KEY, '1')
    else localStorage.removeItem(CLEAR_PENDING_KEY)
  } catch {
    /* storage blocked — the clear itself still runs */
  }
}

/**
 * Delete every IndexedDB database this origin has.
 *
 * The two app databases are deleted SYNCHRONOUSLY, before this returns: log out
 * calls it and navigates away in the same turn, and only a delete that was
 * already issued has a chance to land. Databases the browser lists follow once
 * the listing comes back. The returned promise settles when every delete has
 * completed or been blocked, or after 3 s — for a caller that stays on the page.
 */
export function clearIndexedDb(): Promise<void> {
  if (typeof indexedDB === 'undefined') return Promise.resolve()
  setPending(true)

  const started = new Set<string>()
  const outcomes: Array<Promise<DeleteOutcome>> = []
  const start = (name: string) => {
    if (started.has(name)) return
    started.add(name)
    outcomes.push(deleteDatabase(name))
  }
  APP_DATABASES.forEach(start)

  const all = (async () => {
    try {
      const listed = await indexedDB.databases?.()
      for (const db of listed ?? []) if (db.name) start(db.name)
    } catch {
      /* listing unsupported — the named databases are still cleared */
    }
    const results = await Promise.all(outcomes)
    if (results.every((r) => r === 'deleted')) setPending(false)
  })()

  return Promise.race([
    all,
    new Promise<void>((resolve) => setTimeout(resolve, CLEAR_TIMEOUT_MS)),
  ])
}

/**
 * Finish a log-out clear that did not complete. Called once at app start,
 * before anything has opened a database (Firebase only loads after sign-in), so
 * nothing in this tab can block it.
 */
export function resumePendingLocalDataClear(): Promise<void> {
  let pending = false
  try {
    pending = localStorage.getItem(CLEAR_PENDING_KEY) !== null
  } catch {
    /* storage blocked — no marker can have been written either */
  }
  return pending ? clearIndexedDb() : Promise.resolve()
}
